import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Banknote, CreditCard, Link2, Loader2 } from "lucide-react";

import Modal from "src/components/ui/Modal";
import Input from "src/components/ui/Input";
import Button from "src/components/ui/Button";
import paymentApis from "src/api/payments";
import salesApis from "src/api/salesOrder";
import { currencyFormatter } from "src/utils/common";
import { loadRazorpayCheckout } from "src/utils/loadRazorpay";
import { useAppSelector } from "src/redux/hooks";
import { userCan } from "src/utils/permissions";
import { GatewayStatus, PaymentLink } from "src/types/salePayment.types";
import PaymentLinkModal from "./PaymentLinkModal";

interface CheckoutPaymentModalProps {
  open: boolean;
  order: { _id: string; orderId: string; balanceDue: number };
  // Closed without the payment being completed here (a link may be out).
  onClose: () => void;
  // Money was received — the modal is done.
  onPaid: (result: { amount: number; balanceDue: number; source: "online" | "offline" }) => void;
  // Something changed without finishing (e.g. a link was generated).
  onChanged?: () => void;
}

type Busy = "" | "online" | "verifying" | "offline" | "link";

/**
 * "Collect payment": the amount, then one of three actions —
 * pay online (opens the gateway popup), collect offline / cash, or generate
 * a payment link (opens the link + QR popup).
 */
const CheckoutPaymentModal: React.FC<CheckoutPaymentModalProps> = ({
  open,
  order,
  onClose,
  onPaid,
  onChanged,
}) => {
  const navigate = useNavigate();
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const canSetup = userCan(user, "payments", "setup");

  const [gateway, setGateway] = useState<GatewayStatus | null>(null);
  const [loadingGateway, setLoadingGateway] = useState(false);
  const [amount, setAmount] = useState(String(order.balanceDue));
  const [busy, setBusy] = useState<Busy>("");
  const [error, setError] = useState("");
  const [linkView, setLinkView] = useState<{
    link: PaymentLink;
    customer?: { name?: string; mobile?: string; email?: string };
  } | null>(null);

  const hasGateway = !!gateway?.activeProvider;
  const numericAmount = Number(amount);
  const amountError =
    amount === ""
      ? ""
      : !(numericAmount >= 1)
      ? "Enter at least ₹1"
      : numericAmount > order.balanceDue
      ? `Can't be more than the balance due (${currencyFormatter(order.balanceDue)})`
      : "";
  const amountOk = !!amount && !amountError;
  const amountText = amountOk ? currencyFormatter(numericAmount) : "";

  // Nothing is initiated on open — only when one of the buttons is tapped.
  useEffect(() => {
    if (!open) return;
    setAmount(String(order.balanceDue));
    setError("");
    setBusy("");
    setLinkView(null);
    setLoadingGateway(true);
    paymentApis
      .getGatewayStatus()
      .then((res: any) => setGateway(res?.data || null))
      .catch(() => setGateway(null))
      .finally(() => setLoadingGateway(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const payOnline = async () => {
    setError("");
    setBusy("online");
    try {
      const loaded = await loadRazorpayCheckout();
      if (!loaded) throw new Error("Couldn't load the payment window — check your connection");

      const res: any = await paymentApis.createCheckout(order._id, { amount: numericAmount });
      const data = res?.data;

      const Razorpay = (window as any).Razorpay;
      const rzp = new Razorpay({
        key: data.keyId,
        order_id: data.providerOrderId,
        amount: Math.round(data.amount * 100),
        currency: data.currency,
        name: data.businessName || "Payment",
        description: `Order ${data.orderCode}`,
        prefill: data.prefill,
        handler: async (response: any) => {
          // Money has moved — our server verifies the signature with
          // Razorpay before recording anything.
          setBusy("verifying");
          try {
            const confirm: any = await paymentApis.confirmCheckout(data.checkoutId, {
              providerPaymentId: response.razorpay_payment_id,
              providerOrderId: response.razorpay_order_id,
              signature: response.razorpay_signature,
            });
            onPaid({
              amount: confirm?.data?.amount ?? data.amount,
              balanceDue: confirm?.data?.order?.balanceDue ?? order.balanceDue - data.amount,
              source: "online",
            });
          } catch (err: any) {
            setBusy("");
            setError(
              `Payment was made but couldn't be confirmed yet (${err.message}). It will show on the order once Razorpay notifies us — don't collect it again.`
            );
          }
        },
        modal: { ondismiss: () => setBusy((b) => (b === "verifying" ? b : "")) },
      });
      rzp.on("payment.failed", (resp: any) => {
        setError(
          resp?.error?.description || "Payment failed — try again, or collect it offline"
        );
      });
      rzp.open();
      setBusy("");
    } catch (err: any) {
      setBusy("");
      setError(err.message || "Couldn't start the payment");
    }
  };

  const collectOffline = async () => {
    setError("");
    setBusy("offline");
    try {
      await paymentApis.recordPayment(order._id, { amount: numericAmount, method: "cash" });
      onPaid({
        amount: numericAmount,
        balanceDue: Math.max(0, order.balanceDue - numericAmount),
        source: "offline",
      });
    } catch (err: any) {
      setBusy("");
      setError(err.message || "Failed to record payment");
    }
  };

  const generateLink = async () => {
    setError("");
    setBusy("link");
    try {
      const [linkRes, orderRes]: any[] = await Promise.all([
        paymentApis.createPaymentLink(order._id, {
          amount: numericAmount,
          allowPartial: false,
        }),
        salesApis.getOrderById(order._id).catch(() => null),
      ]);
      const c = orderRes?.data?.customerId;
      setLinkView({
        link: linkRes?.data,
        customer: { name: c?.name, mobile: c?.mobile, email: c?.email },
      });
      onChanged?.();
    } catch (err: any) {
      setError(err.message || "Failed to create payment link");
    } finally {
      setBusy("");
    }
  };

  // The link has its own popup — it replaces this one.
  if (linkView) {
    return (
      <PaymentLinkModal
        open={open}
        link={linkView.link}
        orderCode={order.orderId}
        customer={linkView.customer}
        onClose={onClose}
        onChanged={onChanged}
        onPaid={(received, summary) =>
          onPaid({ amount: received, balanceDue: summary?.balanceDue ?? 0, source: "online" })
        }
      />
    );
  }

  const handleClose = () => {
    if (busy === "verifying" || busy === "offline") return;
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} title="Collect payment">
      {loadingGateway ? (
        <div className="flex justify-center py-10">
          <Loader2 className="animate-spin text-primary-600" size={24} />
        </div>
      ) : busy === "verifying" ? (
        <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-600 dark:text-gray-300">
          <Loader2 size={18} className="animate-spin text-primary-600" />
          Confirming payment…
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between rounded-xl bg-gray-50 px-4 py-3 dark:bg-gray-900/50">
            <div>
              <div className="text-xs uppercase text-gray-400">Order {order.orderId}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Balance due</div>
            </div>
            <div className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              {currencyFormatter(order.balanceDue)}
            </div>
          </div>

          <Input
            label="Amount"
            type="number"
            startAdornment="₹"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            error={!!amountError}
            helperText={
              amountError ||
              (numericAmount < order.balanceDue
                ? "Partial payment — the rest stays due on the order."
                : "Full balance.")
            }
          />

          {error && (
            <div className="flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800 dark:bg-red-900/30 dark:text-red-200">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              {error}
            </div>
          )}

          <div className="space-y-2.5">
            <Button
              fullWidth
              size="lg"
              onClick={payOnline}
              isLoading={busy === "online"}
              disabled={!hasGateway || !amountOk || busy !== ""}
            >
              <CreditCard size={18} className="mr-2" />
              Pay {amountText} online
            </Button>
            <Button
              fullWidth
              size="lg"
              variant="outline"
              onClick={collectOffline}
              isLoading={busy === "offline"}
              disabled={!amountOk || busy !== ""}
            >
              <Banknote size={18} className="mr-2" />
              Collect {amountText} offline / cash
            </Button>
            <Button
              fullWidth
              size="lg"
              variant="outline"
              onClick={generateLink}
              isLoading={busy === "link"}
              disabled={!hasGateway || !amountOk || busy !== ""}
            >
              <Link2 size={18} className="mr-2" />
              Generate payment link
            </Button>
          </div>

          {!hasGateway ? (
            <p className="text-center text-xs text-gray-500 dark:text-gray-400">
              Online payment and links need a payment gateway.
              {canSetup ? (
                <button
                  type="button"
                  className="ml-1 text-primary-600 hover:underline dark:text-primary-400"
                  onClick={() => navigate("/app/settings/payments")}
                >
                  Connect Razorpay
                </button>
              ) : (
                " Ask your admin to connect one in Settings → Payments."
              )}
            </p>
          ) : (
            gateway?.mode === "test" && (
              <p className="text-center text-xs text-yellow-700 dark:text-yellow-400">
                {gateway.displayName} test mode — no real money moves.
              </p>
            )
          )}
        </div>
      )}
    </Modal>
  );
};

export default CheckoutPaymentModal;
